import { createSelector } from "@reduxjs/toolkit";
import { normalizeResourceAllocations } from "./resourceAllocations";

const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const asObject = (value) => (isRecord(value) ? value : {});

const asRecordArray = (value) =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const asPaymentArray = (value) => {
  if (Array.isArray(value)) return value.filter(isRecord);
  return isRecord(value) && Object.keys(value).length > 0 ? [value] : [];
};

const appointmentObject = (value) => {
  if (Array.isArray(value)) {
    return value.find(isRecord) || {};
  }
  const source = asObject(value);
  if (!source.id && Array.isArray(source.data)) {
    return source.data.find(isRecord) || {};
  }
  if (!source.id && isRecord(source.data)) {
    return source.data;
  }
  return source;
};

const normalizeServiceRow = (row) => ({
  ...row,
  service: asObject(row.service),
  staff: asObject(row.staff),
  resource_item: asObject(row.resource_item),
  resource_allocations: normalizeResourceAllocations(row),
});

export const normalizeAppointmentDetail = (detail) => {
  const source = appointmentObject(detail);
  const payments = source.payment ?? source.payments;

  return {
    ...source,
    // Laravel-shaped appointment details can expose one payment object while
    // Flask exposes a payment list. Preserve both forms for refund detection.
    payment: asPaymentArray(payments).map((payment) => ({ ...payment })),
    addonservices: asRecordArray(source.addonservices).map(normalizeServiceRow),
    addanotherservices: asRecordArray(source.addanotherservices).map(normalizeServiceRow),
    client: asObject(source.client),
    service: asObject(source.service),
    staff: asObject(source.staff),
    sale: asObject(source.sale),
    resource_item: asObject(source.resource_item),
    resource_allocations: normalizeResourceAllocations(source),
    salon: asObject(source.salon),
  };
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || String(value || "").toLowerCase() === "true";

// A voucher-funded appointment deposit was historically stored as a
// ``Voucher`` payment row (is_deposit true) instead of a ``Deposit`` row, so
// the deposit balance and refund flow never saw it.
const isDepositPaymentRow = (payment) => {
  const type = String(payment?.type || "").trim().toLowerCase();
  if (type === "deposit") return true;
  if (type === "voucher") return isTruthyFlag(payment?.is_deposit);
  return false;
};

export const getAppointmentDepositSummary = (detail) => {
  const normalized = normalizeAppointmentDetail(detail);
  const amount = (payment) => {
    const parsed = Number.parseFloat(payment?.amount);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const depositPaid = normalized.payment
    .filter((payment) => isDepositPaymentRow(payment) && String(payment.status || "").toLowerCase() === "paid")
    .reduce((sum, payment) => sum + amount(payment), 0);
  const depositRefund = normalized.payment
    .filter((payment) => String(payment.type || "").toLowerCase() === "depositrefund" && String(payment.status || "").toLowerCase() === "paid")
    .reduce((sum, payment) => sum + Math.abs(amount(payment)), 0);
  const depositPayment = [...normalized.payment]
    .reverse()
    .find((payment) => isDepositPaymentRow(payment) && String(payment.status || "").toLowerCase() === "paid" && amount(payment) > 0);

  return {
    depositPaid,
    depositRefund,
    depositBalance: Math.max(0, depositPaid - depositRefund),
    depositPaymentBy: depositPayment?.paidby
      || depositPayment?.paid_by
      || (String(depositPayment?.type || "").toLowerCase() === "voucher" ? "Voucher" : ""),
  };
};

/** Funding totals come from the tenant-scoped backend ledger projection. */
export const getVoucherDepositFunding = (detail) => {
  const funding = normalizeAppointmentDetail(detail).deposit_funding;
  return isRecord(funding) && typeof funding.covered === "boolean" ? funding : null;
};

export const formatDepositRemainingBalance = (funding) => {
  if (funding?.remaining_balance === null || funding?.remaining_balance === undefined
      || !Number.isFinite(Number(funding.remaining_balance)) || !/^[A-Z]{3}$/.test(funding?.currency || "")) return "";
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: funding.currency, currencyDisplay: "code" })
      .format(Number(funding.remaining_balance));
  } catch (_) { return ""; }
};

/** Net paid deposit by method for the calendar drawer's mixed-voucher receipt. */
export const getAppointmentDepositBreakdown = (detail) => {
  const payments = normalizeAppointmentDetail(detail).payment;
  const totals = new Map();
  let unattributedRefund = false;
  payments.forEach((payment) => {
    if (String(payment.status || "").toLowerCase() !== "paid") return;
    const type = String(payment.type || "").toLowerCase();
    if (!isDepositPaymentRow(payment) && type !== "depositrefund") return;
    const amount = Number.parseFloat(payment.amount);
    if (!Number.isFinite(amount)) return;
    if (type === "depositrefund" && !payment.paidby && !payment.paid_by) {
      unattributedRefund = true;
      return;
    }
    const method = String(payment.paidby || payment.paid_by || (type === "voucher" ? "Voucher" : "Other"));
    const movement = type === "depositrefund" ? -Math.abs(amount) : Math.max(0, amount);
    totals.set(method, (totals.get(method) || 0) + movement);
  });
  const rows = [...totals].filter(([, amount]) => amount > 0);
  const netDeposit = getAppointmentDepositSummary(detail).depositBalance;
  if (unattributedRefund || Math.abs(rows.reduce((sum, [, amount]) => sum + amount, 0) - netDeposit) > 0.005) {
    return [];
  }
  return rows.map(([method, amount]) => ({
    method,
    label: method === "StripeCreditCard" || method === "CreditCard" ? "Credit or debit card" : method,
    amount,
  }));
};

const selectAppointmentDetailValue = (state) =>
  state?.appointment?.isDetailData;

export const selectSafeAppointmentDetail = createSelector(
  [selectAppointmentDetailValue],
  normalizeAppointmentDetail,
);

export const normalizeCancellationReasons = (value) => {
  const source = asObject(value);
  return asRecordArray(source.data || value);
};
